import { ElementRef, Optional, Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngrx/store';
import { first, map, Observable } from 'rxjs';

import { fromCarl } from 'src/app/store/common/common.selectors';

import { RETURN_TRANSLATION_OR_KEY } from '../translate/translate.pipe';

/**
 * TranslateAndObservePipe is an Angular pipe used for the asynchronous translation of texts.
 * It subscribes to a value from the NgRx store based on a given key and optional parameters.
 * This makes it possible to integrate reactive translations into Angular templates.
 *
 * @example
 * {{ 'HELLO_WORLD' | translateAndObserve:'param1':'param2' | async}}
 */
@Pipe({
  name: 'translateAndObserve',
  pure: true, // intentionally keeping it pure(true) and returning an observable that it still can react on changes in the store like a runtime carl translation change
  standalone: true
})
export class TranslateAndObservePipe implements PipeTransform {
  public constructor(
    private readonly store: Store,
    @Optional() private readonly elementRef?: ElementRef<HTMLElement>
  ) {}
  public transform(key: string, ...params: string[]): Observable<string> {
    /**
     * Sets the used translation key as data attribute of the current node or, if
     * the node is not an element, the parent element of the current node.
     *
     * Used for QA for selecting elements.
     *
     * @param ref - The translation key with its optional params
     */
    const setMarkupReference = (ref: string): void => {
      let element = this.elementRef?.nativeElement;
      if (element) {
        // if current node is no element, find the next parent element
        while (element.nodeName.startsWith('#') && element.parentElement) {
          element = element.parentElement;
        }

        element.dataset.translateKeys = `["${ref}"]`;
      }
    };

    const hasReturnTranslationOrKeyParam = (): boolean => {
      const returnTranslationOrKeyIdx = params.indexOf(RETURN_TRANSLATION_OR_KEY);
      if (returnTranslationOrKeyIdx !== -1) {
        // remove the param from the list to avoid conflict on translation
        params.splice(returnTranslationOrKeyIdx, 1);
      }

      return returnTranslationOrKeyIdx !== -1;
    };

    const shouldReturnTranslationOrKey = hasReturnTranslationOrKeyParam();

    const reference = [key, ...params].filter(Boolean).join(':');
    setMarkupReference(reference);

    return this.store
      .select(fromCarl.selectTranslation(key, ...params))
      .pipe(first())
      .pipe(
        map(translationFromStore => {
          let translation = '';

          if (translationFromStore) {
            translation = translationFromStore;
          } else if (shouldReturnTranslationOrKey) {
            translation = key;
          }

          return translation;
        })
      );
  }
}
